import {
  buySubmissionFormEventDataSchema,
  FingerprintData,
  sellSubmissionFormEventDataSchema,
} from "@/core";
import { ProductData } from "@/core/entities/product";
import { StatusTier } from "@/core/dtos/status-tier";
import { TransactionTypes } from "@/stores";
import { z } from "zod";

export interface MilesProgram extends ProductData {
  id: string;
  programId: string;
  airline: string;
  logo: string;
  loyaltyStatus: StatusTier | null;
  ratePerMile: number;
  statusBonus: number;
  totalPayout: number;
  fingerprint: FingerprintData | null;
  accountDetails:
    | Omit<
        z.infer<typeof buySubmissionFormEventDataSchema.shape.data>,
        "miles" | "averageRate" | "totalPayout" | "fingerprint"
      >
    | (Omit<
        z.infer<typeof sellSubmissionFormEventDataSchema.shape.data>,
        | "miles"
        | "averageRate"
        | "totalPayout"
        | "fingerprint"
        | "paymentMethod"
      > & {
        paymentMethod?: z.infer<
          typeof sellSubmissionFormEventDataSchema.shape.data
        >["paymentMethod"];
      });
  sanitizedMiles: number;
  promoCode?: {
    id: string;
    code: string;
    discount:
      | number
      | {
          type: "percentage" | "fixed";
          value: number;
        };
    name: string;
    enabledFor?: string;
    expirationDate?: Date;
  };
}

export function calculateDynamicRate({
  miles,
  transactionType,
  sellPrices,
  buyPrices,
  fallbackRate,
}: {
  miles: number;
  transactionType: TransactionTypes;
  sellPrices: Array<{ minRange: number; maxRange: number; price: number }>;
  buyPrices: Array<{ minRange: number; maxRange: number; price: number }>;
  fallbackRate: number;
}): number {
  const prices =
    transactionType === TransactionTypes.BUY ? buyPrices : sellPrices;

  if (!prices || prices.length === 0) {
    return fallbackRate; // fallback to default rate
  }
  // Find the appropriate price tier for the given miles
  for (const tier of prices) {
    if (miles >= tier.minRange && miles <= tier.maxRange) {
      return tier.price;
    }
  }

  // If no tier matches, use the last tier's price or fallback
  return prices[prices.length - 1]?.price || fallbackRate;
}

/**
 * Calculate total payout for a given miles amount
 *
 * @param totalMiles The number of miles
 * @param dynamicRate The rate per 100 miles
 * @param loyaltyStatus The loyalty status of the user
 * @param transactionType The transaction type (buy or sell)
 *
 * @return Object containing base amount, status bonus amount, and total payout
 */
/**
 * Get the status bonus percentage based on the loyalty status tier
 *
 * @param statusTier The loyalty status tier object
 * @return The bonus percentage for the given status tier
 */
export function getStatusBonus(statusTier: StatusTier | null): number {
  if (!statusTier) return 0;
  return statusTier.percentage;
}

export function updateProgramStatus(
  program: MilesProgram,
  newStatus: StatusTier | null,
): MilesProgram {
  return {
    ...program,
    loyaltyStatus: newStatus,
  };
}

export function calculateTotalPayout({
  totalMiles,
  dynamicRate,
  loyaltyStatus,
  transactionType,
  promoCode,
}: {
  totalMiles: number;
  dynamicRate: number;
  loyaltyStatus: StatusTier | null;
  transactionType: TransactionTypes;
  promoCode?: {
    id: string;
    code: string;
    discount:
      | number
      | {
          type: "percentage" | "fixed";
          value: number;
        };
    name: string;
    enabledFor?: string;
    expirationDate?: Date;
  };
}): {
  baseAmount: number;
  statusBonusAmount: number;
  promoCodeDiscount: number;
  totalPayout: number;
} {
  // Calculate status bonus percentage
  const statusBonusPercentage = getStatusBonus(loyaltyStatus);

  // Calculate base amount
  const baseAmount = totalMiles * dynamicRate;

  // Calculate status bonus amount
  const statusBonusAmount = baseAmount * (statusBonusPercentage / 100);

  // Use promo code discount if provided, otherwise 0.
  // Legacy deals store a precomputed signed number.
  // Temporary deals may store discount rules and we compute sign at runtime.
  let promoDiscount = 0;
  if (promoCode) {
    if (typeof promoCode.discount === "number") {
      promoDiscount = promoCode.discount;
    } else {
      const rawDiscount =
        promoCode.discount.type === "percentage"
          ? baseAmount * (promoCode.discount.value / 100)
          : promoCode.discount.value;
      promoDiscount =
        transactionType === TransactionTypes.SELL ? rawDiscount : -rawDiscount;
    }
  }

  // Calculate total payout (promo code discount is already signed correctly)
  const totalPayout = baseAmount + statusBonusAmount + promoDiscount;

  return {
    baseAmount,
    statusBonusAmount,
    promoCodeDiscount: promoDiscount,
    totalPayout,
  };
}
